import {AfterViewInit, Component, HostListener, Inject, OnDestroy, OnInit, Renderer2} from '@angular/core';
import {SidebarService} from '../services/side-bar.service';
import {TaskBarService} from '../services/task-bar.service';
import {MobileMenuService} from '../services/mobile-menu.service';
import {Location} from '@angular/common';
import {ActivatedRoute, NavigationEnd, NavigationStart, Router, RoutesRecognized} from '@angular/router';
import {DOCUMENT} from '@angular/platform-browser';
import {Subject} from 'rxjs/Subject';
import 'rxjs/add/operator/filter';
import 'rxjs/add/operator/pairwise';
import 'rxjs/add/operator/takeUntil';

@Component({
  selector: 'app-content-page',
  templateUrl: './content-page.component.html',
  styleUrls: ['./content-page.component.css']
})
export class ContentPageComponent implements OnInit, OnDestroy, AfterViewInit {
  loadedFeature = 'home-task';
  previousRoute: string;
  nextRoute: string;
  isFullscreenLayout = false;
  private destroy$ = new Subject<void>();
  private mobileShellTopTimers: any[] = [];
  private mobileHeaderObserver: MutationObserver;

  constructor(
    public sidebarService: SidebarService,
    public taskbarService: TaskBarService,
    public mobileMenuService: MobileMenuService,
    private _location: Location,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private renderer: Renderer2,
    @Inject(DOCUMENT) private document: Document
  ) {}

  ngOnInit() {
    this.trackPreviousRoute();
    this.trackNextRoute();
    this.trackLayoutMode();
    this.updateLayoutMode();
  }

  ngAfterViewInit() {
    this.syncMobileShellTop();
    this.observeMobileHeader();
    this.scheduleMobileShellTopSync();
  }

  ngOnDestroy() {
    this.clearMobileShellTopTimers();
    this.disconnectMobileHeaderObserver();
    this.document.documentElement.style.removeProperty('--mobile-shell-top');
    this.removeBodyFullscreenClass();
    this.destroy$.next();
    this.destroy$.complete();
  }

  @HostListener('window:resize')
  onWindowResize() {
    this.syncMobileShellTop();
    this.scheduleMobileShellTopSync();
  }

  onNavigate(feature: string) {
    this.loadedFeature = feature;
  }

  goBack() {
    this._location.back();
  }

  backToList() {
    this.router.navigateByUrl('/dashboard/operating-standards');
  }

  private trackPreviousRoute() {
    this.router.events
      .filter(e => e instanceof RoutesRecognized)
      .pairwise()
      .takeUntil(this.destroy$)
      .subscribe((event: any[]) => this.persistPreviousRoute(event));
  }

  private trackNextRoute() {
    this.router.events
      .filter(e => e instanceof NavigationStart)
      .pairwise()
      .takeUntil(this.destroy$)
      .subscribe((event: any[]) => this.persistNextRoute(event));
  }

  private trackLayoutMode() {
    this.router.events
      .filter(e => e instanceof NavigationEnd)
      .takeUntil(this.destroy$)
      .subscribe(() => {
        this.updateLayoutMode();
        this.syncMobileShellTop();
        this.scheduleMobileShellTopSync();
      });
  }

  private persistPreviousRoute(event: any[]) {
    this.previousRoute = event[0].urlAfterRedirects;
    localStorage.setItem('prevRoute', this.previousRoute);
  }

  private persistNextRoute(event: any[]) {
    this.nextRoute = event[1].url;
    localStorage.setItem('nextRoute', this.nextRoute);
  }

  private updateLayoutMode() {
    this.isFullscreenLayout = this.hasFullscreenLayout();
    this.syncBodyLayoutClass();
  }

  private hasFullscreenLayout(): boolean {
    let route: ActivatedRoute;
    route = this.activatedRoute;
    while (route.firstChild) {
      route = route.firstChild;
    }
    return !!route.snapshot.data.fullscreenLayout;
  }

  private syncBodyLayoutClass() {
    if (this.isFullscreenLayout) {
      this.renderer.addClass(this.document.body, 'body--fullscreen-layout');
      return;
    }
    this.removeBodyFullscreenClass();
  }

  private removeBodyFullscreenClass() {
    this.renderer.removeClass(this.document.body, 'body--fullscreen-layout');
  }

  private scheduleMobileShellTopSync() {
    this.clearMobileShellTopTimers();
    [0, 100, 300, 1000].forEach((delay: number) => {
      this.mobileShellTopTimers.push(setTimeout(() => this.syncMobileShellTop(), delay));
    });
  }

  private clearMobileShellTopTimers() {
    while (this.mobileShellTopTimers.length > 0) {
      clearTimeout(this.mobileShellTopTimers.pop());
    }
  }

  private syncMobileShellTop() {
    const header = this.getMobileHeader();
    if (!header || !header.getBoundingClientRect) {
      return;
    }
    const height = Math.ceil(header.getBoundingClientRect().height);
    if (height > 0) {
      this.document.documentElement.style.setProperty('--mobile-shell-top', height + 'px');
    }
  }

  private observeMobileHeader() {
    const header = this.getMobileHeader();
    if (!header || typeof MutationObserver === 'undefined') {
      return;
    }
    this.disconnectMobileHeaderObserver();
    this.mobileHeaderObserver = new MutationObserver(() => this.scheduleMobileShellTopSync());
    this.mobileHeaderObserver.observe(header, {
      attributes: true,
      childList: true,
      characterData: true,
      subtree: true
    });
  }

  private disconnectMobileHeaderObserver() {
    if (!this.mobileHeaderObserver) {
      return;
    }
    this.mobileHeaderObserver.disconnect();
    this.mobileHeaderObserver = null;
  }

  private getMobileHeader(): HTMLElement {
    return this.document.querySelector('app-header .navbar.fixed-top') as HTMLElement;
  }

  public isBackBtnHidden() {
    let list: string[], route: string;
    list = [
      "/dashboard/testing",
      "/dashboard",
      "/dashboard/home",
      "/dashboard/groups",
      "/dashboard/operating-standard",
      "/dashboard/standards",
      "/dashboard/company-structure",
      "/dashboard/admin/register-user",
      "/dashboard/admin/create-group",
      "/dashboard/admin/create-position",
      "/dashboard/admin/create-schedule",
      "/dashboard/admin/create-standard",
      "/dashboard/admin/all-standards",
      "/dashboard/admin/all-standards-head",
      "/dashboard/operating-standards",
      "/dashboard/profile",
      "/dashboard/create-operating-standard",
      "/dashboard/create-operating-standard/step-two",
      "/dashboard/create-operating-standard/step-three",
      "/dashboard/create-operating-standard/step-four",
      "/dashboard/create-operating-standard/step-five",
      "/dashboard/create-operating-standard/step-six",
      "/dashboard/create-operating-standard/step-seven",
      "/dashboard/create-operating-standard/step-eight",
      "/dashboard/create-operating-standard/add-user-steps",
      "/dashboard/specializations-list",
      "/dashboard/create-suggestion-list",
      "/dashboard/my-suggestion-lists",
      "/dashboard/all-suggestion-lists",
      "/dashboard/admin/ai-statistics",
      "/dashboard/add-specialization",
      "/dashboard/learning",
      "/dashboard/edit-specialization",
      "/dashboard/start-learning",
      "/dashboard/admin/users"
    ];
    route = this._location.path();
    // I have no excuse for this
    if (list.indexOf(route) > -1) {
      return !(route === '/dashboard/standards' && localStorage.getItem('prevRoute') === '/dashboard/testing');
    } else {
      return false;
    }
  }

  public backToListHidden() {
    let list: string[], route: string;
    list = [
      "/dashboard/create-operating-standard",
      "/dashboard/create-operating-standard/step-two",
      "/dashboard/create-operating-standard/step-three",
      "/dashboard/create-operating-standard/step-four",
      "/dashboard/create-operating-standard/step-five",
      "/dashboard/create-operating-standard/step-six",
      "/dashboard/create-operating-standard/step-seven",
      "/dashboard/create-operating-standard/step-eight",
      "/dashboard/create-operating-standard/add-user-steps"
    ];
    route = this._location.path();
    if (list.indexOf(route) > -1) {
      return false;
    } else {
      return true;
    }
  }

}



// WEBPACK FOOTER //
// C:/work/internal-frontend/src/app/content-page/content-page.component.ts